/**
 * Analytics Service for Google Analytics 4 Integration
 * Handles user segmentation and event tracking for promo code users
 */

// User segment types
export interface UserSegment {
  segment: 'promo_code' | 'standard';
  promoCode?: string;
  bankPartner?: 'greater_bank' | 'newcastle_permanent' | null;
  subscriptionStatus: 'trial' | 'premium' | 'expired' | 'inactive';
}

// Analytics event parameters
export interface AnalyticsEventParams {
  [key: string]: any;
  user_segment?: string;
  promo_code_type?: string;
  bank_partner?: string;
  subscription_status?: string;
}

/**
 * Maps promo codes to bank partners
 */
const PROMO_CODE_TO_BANK_PARTNER: Record<string, string> = {
  'HERE4U': 'newcastle_permanent',
  'GREATER4U': 'greater_bank',
};

/**
 * Determines user segment based on user data
 */
export function getUserSegment(userData: any): UserSegment {
  const promoCode = userData?.promo_code;
  const isPremium = userData?.isPremium;
  const isSubscriptionValid = userData?.is_subscription_valid;
  const trialStartedAt = userData?.trialStartedAt;

  // Determine subscription status
  let subscriptionStatus: UserSegment['subscriptionStatus'] = 'inactive';
  
  // Check if user is on an active trial (promo code users get 6-month trials)
  if (trialStartedAt) {
    const trialStart = new Date(trialStartedAt);
    const now = new Date();
    const daysSinceTrialStart = Math.floor((now.getTime() - trialStart.getTime()) / (1000 * 60 * 60 * 24));
    const trialDays = promoCode ? 180 : 30; // Promo code users get 180 days, others get 30
    
    if (daysSinceTrialStart <= trialDays) {
      subscriptionStatus = 'trial';
    } else if (isPremium && isSubscriptionValid) {
      subscriptionStatus = 'premium';
    } else if (isPremium && !isSubscriptionValid) {
      subscriptionStatus = 'expired';
    }
  } else if (isPremium && isSubscriptionValid) {
    subscriptionStatus = 'premium';
  } else if (isPremium && !isSubscriptionValid) {
    subscriptionStatus = 'expired';
  }

  // Determine if user is promo code user
  const isPromoCodeUser = promoCode && promoCode.trim() !== '';
  const bankPartner = isPromoCodeUser ? PROMO_CODE_TO_BANK_PARTNER[promoCode] || null : null;

  return {
    segment: isPromoCodeUser ? 'promo_code' : 'standard',
    promoCode: isPromoCodeUser ? promoCode : undefined,
    bankPartner: bankPartner as UserSegment['bankPartner'],
    subscriptionStatus
  };
}

/**
 * Sets user properties in Google Analytics
 */
export function setUserProperties(userSegment: UserSegment): void {
  console.log('🔍 DEBUG: setUserProperties called with:', userSegment);
  console.log('🔍 DEBUG: window.gtag available?', typeof window !== 'undefined' && !!window.gtag);
  console.log('🔍 DEBUG: window object:', typeof window);
  
  if (typeof window === 'undefined') {
    console.warn('❌ Window is undefined - running on server side');
    return;
  }
  
  // Check if we can access GTM's dataLayer or gtag
  const hasGTMDataLayer = (window as any).google_tag_manager?.dataLayer;
  const hasGlobalDataLayer = (window as any).dataLayer;
  const hasGtag = (window as any).gtag;
  
  if (!hasGtag && !hasGTMDataLayer && !hasGlobalDataLayer) {
    console.warn('❌ No analytics method available (gtag, GTM dataLayer, or global dataLayer)');
    return;
  }
  
  console.log('🔍 Analytics methods available:', {
    gtag: !!hasGtag,
    gtmDataLayer: !!hasGTMDataLayer, 
    globalDataLayer: !!hasGlobalDataLayer
  });

  try {
    console.log('✅ Sending user properties to Google Analytics...');
    
    const eventData = {
      event: 'set_user_properties',
      user_segment: userSegment.segment,
      promo_code_type: userSegment.promoCode || 'none',
      bank_partner: userSegment.bankPartner || 'none',
      subscription_status: userSegment.subscriptionStatus || 'inactive'
    };

    // Try gtag first (if available)
    if (hasGtag) {
      (window as any).gtag('config', 'G-NR2SKYLR8V', {
        custom_map: {
          custom_dimension_1: 'user_segment',
          custom_dimension_2: 'promo_code_type',
          custom_dimension_3: 'bank_partner',
          custom_dimension_4: 'subscription_status'
        }
      });
      
      (window as any).gtag('set', {
        user_segment: userSegment.segment,
        promo_code_type: userSegment.promoCode || 'none',
        bank_partner: userSegment.bankPartner || 'none',
        subscription_status: userSegment.subscriptionStatus
      });
      console.log('✅ User properties sent via gtag:', userSegment);
    }
    // Fallback to dataLayer push
    else if (hasGlobalDataLayer) {
      (window as any).dataLayer.push(eventData);
      console.log('✅ User properties sent via global dataLayer:', eventData);
    }
    // Last resort: GTM's internal dataLayer
    else if (hasGTMDataLayer) {
      // GTM's dataLayer might need different access
      (window as any).google_tag_manager.dataLayer.push?.(eventData);
      console.log('✅ User properties sent via GTM dataLayer:', eventData);
    }
  } catch (error) {
    console.error('❌ Error setting user properties:', error);
  }
}

/**
 * Identifies and tags a user in Google Analytics
 */
export function identifyUser(userData: any): UserSegment {
  const userSegment = getUserSegment(userData);
  setUserProperties(userSegment);
  
  // Track user identification event
  trackEvent('user_identified', {
    user_id: userData?.id?.toString(),
    email: userData?.email,
    user_segment: userSegment.segment,
    promo_code_type: userSegment.promoCode || 'none',
    bank_partner: userSegment.bankPartner || 'none',
    subscription_status: userSegment.subscriptionStatus
  });

  return userSegment;
}

/**
 * Tracks an event with user segmentation data
 */
export function trackEvent(eventName: string, parameters: AnalyticsEventParams = {}, userSegment?: UserSegment): void {
  console.log(`🔍 DEBUG: trackEvent called - ${eventName}`, { parameters, userSegment });
  console.log('🔍 DEBUG: window.gtag available?', typeof window !== 'undefined' && !!window.gtag);
  
  if (typeof window === 'undefined') {
    console.warn('❌ Window is undefined - running on server side');
    return;
  }
  
  if (!window.gtag) {
    console.warn('❌ Google Analytics gtag function not available for event tracking');
    console.log('🔍 DEBUG: Available window properties:', Object.keys(window).filter(key => key.includes('gtag') || key.includes('ga')));
    return;
  }

  try {
    // Add user segment data to parameters if provided
    const enrichedParams = { ...parameters };
    if (userSegment) {
      enrichedParams.user_segment = userSegment.segment;
      enrichedParams.promo_code_type = userSegment.promoCode || 'none';
      enrichedParams.bank_partner = userSegment.bankPartner || 'none';
      enrichedParams.subscription_status = userSegment.subscriptionStatus;
    }

    console.log(`✅ Sending event to Google Analytics: ${eventName}`, enrichedParams);
    window.gtag('event', eventName, enrichedParams);
    console.log(`✅ Analytics: Event tracked successfully - ${eventName}`, enrichedParams);
  } catch (error) {
    console.error(`❌ Error tracking event ${eventName}:`, error);
  }
}

/**
 * Tracks promo code usage
 */
export function trackPromoCodeUsage(promoCode: string, isValid: boolean, trialDays?: number): void {
  const bankPartner = PROMO_CODE_TO_BANK_PARTNER[promoCode] || 'unknown';
  
  trackEvent('promo_code_used', {
    promo_code: promoCode,
    is_valid: isValid,
    bank_partner: bankPartner,
    trial_days: trialDays || 0,
    event_category: 'engagement',
    event_label: `${promoCode}_${isValid ? 'valid' : 'invalid'}`
  });
}

/**
 * Tracks product interactions with user segmentation
 */
export function trackProductInteraction(
  action: 'view' | 'add_to_basket' | 'remove_from_basket',
  product: any,
  userSegment?: UserSegment
): void {
  const eventName = action === 'view' ? 'view_item' : 
                   action === 'add_to_basket' ? 'add_to_cart' : 'remove_from_cart';
  
  trackEvent(eventName, {
    item_id: product.id,
    item_name: product.title || product.name,
    item_category: product.category || 'grocery',
    price: product.price || 0,
    currency: 'AUD',
    event_category: 'ecommerce',
    event_label: product.title || product.name
  }, userSegment);
}

/**
 * Tracks checkout events
 */
export function trackCheckoutEvent(
  step: 'begin_checkout' | 'add_payment_info' | 'purchase',
  value?: number,
  promoCode?: string,
  userSegment?: UserSegment
): void {
  const params: AnalyticsEventParams = {
    currency: 'AUD',
    event_category: 'ecommerce'
  };

  if (value !== undefined) {
    params.value = value;
  }

  if (promoCode) {
    params.coupon = promoCode;
    params.promo_code = promoCode;
  }

  trackEvent(step, params, userSegment);
}

/**
 * Tracks search events with user context
 */
export function trackSearchEvent(
  searchTerm: string,
  resultsCount: number,
  userSegment?: UserSegment
): void {
  trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount,
    event_category: 'engagement',
    event_label: searchTerm
  }, userSegment);
}

/**
 * Tracks page views with user segmentation
 */
export function trackPageView(
  pagePath: string,
  pageTitle?: string,
  userSegment?: UserSegment
): void {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  const params: AnalyticsEventParams = {
    page_path: pagePath,
    page_title: pageTitle || document.title,
    event_category: 'engagement'
  };

  if (userSegment) {
    params.user_segment = userSegment.segment;
    params.promo_code_type = userSegment.promoCode || 'none';
    params.bank_partner = userSegment.bankPartner || 'none';
    params.subscription_status = userSegment.subscriptionStatus;
  }

  window.gtag('config', 'G-NR2SKYLR8V', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
    ...params
  });
}

/**
 * Tracks Grocerize List clicks (compare grocery prices)
 */
export function trackGrocerizeListClick(
  listId?: string,
  listName?: string,
  itemCount?: number,
  userSegment?: UserSegment
): void {
  trackEvent('grocerize_list_click', {
    list_id: listId,
    list_name: listName,
    item_count: itemCount || 0,
    event_category: 'engagement',
    event_label: listName || 'grocerize_list',
    action: 'compare_prices'
  }, userSegment);
}

/**
 * Tracks category browsing clicks
 */
export function trackCategoryClick(
  categoryName: string,
  categoryPath?: string,
  userSegment?: UserSegment
): void {
  trackEvent('category_click', {
    category_name: categoryName,
    category_path: categoryPath || categoryName.toLowerCase().replace(/\s+/g, '_'),
    event_category: 'navigation',
    event_label: categoryName,
    action: 'browse_category'
  }, userSegment);
}

/**
 * Tracks navigation events (general purpose)
 */
export function trackNavigationEvent(
  action: string,
  destination: string,
  source?: string,
  userSegment?: UserSegment
): void {
  trackEvent('navigation', {
    navigation_action: action,
    destination: destination,
    source: source || 'unknown',
    event_category: 'navigation',
    event_label: `${action}_${destination}`
  }, userSegment);
}

/**
 * Test function to verify Google Analytics is working
 */
export function testGoogleAnalytics(): void {
  console.log('🧪 Testing Google Analytics...');
  console.log('🔍 Window available?', typeof window !== 'undefined');
  console.log('🔍 gtag available?', typeof window !== 'undefined' && !!window.gtag);
  
  if (typeof window !== 'undefined' && window.gtag) {
    console.log('✅ Sending test event to Google Analytics...');
    window.gtag('event', 'test_analytics', {
      event_category: 'debug',
      event_label: 'analytics_test',
      value: 1
    });
    console.log('✅ Test event sent!');
  } else {
    console.error('❌ Google Analytics not available for testing');
  }
}

/**
 * Gets current user segment from Redux state (utility function)
 */
export function getCurrentUserSegment(): UserSegment | null {
  console.log('🔍 DEBUG: getCurrentUserSegment called');
  
  try {
    // Try to access Redux store from window if available
    if (typeof window !== 'undefined' && (window as any).__REDUX_STORE__) {
      const store = (window as any).__REDUX_STORE__;
      const state = store.getState();
      const userData = state.user;
      console.log('🔍 DEBUG: Found user data from Redux:', userData);
      return getUserSegment(userData);
    }
    
    console.warn('⚠️ Redux store not available in getCurrentUserSegment');
    return null;
  } catch (error) {
    console.error('❌ Error getting current user segment:', error);
    return null;
  }
}

// Export analytics service as default
const analyticsService = {
  getUserSegment,
  setUserProperties,
  identifyUser,
  trackEvent,
  trackPromoCodeUsage,
  trackProductInteraction,
  trackCheckoutEvent,
  trackSearchEvent,
  trackPageView,
  trackGrocerizeListClick,
  trackCategoryClick,
  trackNavigationEvent,
  getCurrentUserSegment,
  testGoogleAnalytics
};

export default analyticsService;
